import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { EmptyState, Tooltip } from '../../ui';
import IronSightMark from '../../shared/IronSightMark';
import { formatCurrency, validPrice } from '../../../utils/formatting';
import {
    OUTCOME_LABELS, PIPELINE_STEP_DURATIONS, PIPELINE_THINKING_STEPS, PIPELINE_TIPS, fmtMoney, fmtPipelineDate,
    gapBand, groupMachineQuotes, isOtherUnitRow, meetsAiEstimate, quoteChartPoints, statusTone, withinTenPct,
} from '../../../utils/unitPipeline';
import { UnitChart, niceTicks } from '../charts';
import { BoltIcon } from '../icons';
import StepLoader from './serviceThinking';
import { tabCtxShape } from './tabContext';
import './PipelineTabV2.css';

const OUTCOME_TONES = { open: 'teal', lost: 'red', nosale: 'grey', closed: 'slate' };

function StatusPill({ status }) {
    if (!status) return null;
    return <span className={`u2-pl-pill is-${statusTone(status)}`}>{status}</span>;
}
StatusPill.propTypes = { status: PropTypes.string };

function GapBadge({ gap }) {
    const { band, label } = gapBand(gap);
    return (
        <Tooltip content={`Gap vs asking price: ${label}`}>
            <span className={`u2-pl-gap is-${band}`} tabIndex={0}>{label}</span>
        </Tooltip>
    );
}
GapBadge.propTypes = { gap: PropTypes.number };

/** One machine's line on a deal (P-UNIT-090). */
function MachineRow({ q, aiPrice, stock }) {
    const other = isOtherUnitRow(q, stock);
    const aboveAi = aiPrice != null && q.selling_price != null && q.selling_price >= aiPrice;
    return (
        <div className={`u2-pl-mq${other ? ' is-other' : ''}`}>
            <div className="u2-pl-row">
                <span className="u2-pl-mq-main">
                    <b>{fmtMoney(q.selling_price)}</b>
                    {other ? (
                        <Tooltip content={`This price is for unit #${q.unit_stock_number} in the same deal`}>
                            <span className="u2-pl-tag" tabIndex={0}>#{q.unit_stock_number}</span>
                        </Tooltip>
                    ) : (
                        <span className="u2-pl-tag is-this">This unit</span>
                    )}
                    <StatusPill status={q.quote_status} />
                    {aboveAi && !other && (
                        <span className="u2-pl-pill is-ai" title={`Buyer quoted above AI estimated price of ${fmtMoney(aiPrice)}`}>≥ AI Price</span>
                    )}
                    <span className="u2-quiet">{fmtPipelineDate(q.created_date)}</span>
                    {q.hours != null && q.hours > 0 && <span className="u2-pl-chip">{q.hours.toLocaleString('en-US')} hrs</span>}
                </span>
                <GapBadge gap={q.price_gap_pct} />
            </div>
        </div>
    );
}
MachineRow.propTypes = { q: PropTypes.object.isRequired, aiPrice: PropTypes.number, stock: PropTypes.string };

/** Collapsible trade-ins of a deal (P-UNIT-091). */
function TradeIns({ tradeIns, id }) {
    const [open, setOpen] = useState(false);
    if (!tradeIns || tradeIns.length === 0) return null;
    const panelId = `ti-${id}`;
    return (
        <div className="u2-pl-ti-wrap">
            <button type="button" className="u2-pl-disc" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen((v) => !v)}>
                {open ? '▲' : '▼'} Trade-ins ({tradeIns.length})
            </button>
            {open && (
                <div id={panelId} className="u2-pl-tis">
                    {tradeIns.map((ti) => {
                        const title = [ti.year, ti.make, ti.model].filter(Boolean).join(' ') || 'Unknown unit';
                        const over = ti.overallowance != null && ti.overallowance > 0;
                        const payoff = ti.payoff_amount != null && ti.payoff_amount > 0;
                        return (
                            <div key={ti.trade_in_id} className="u2-pl-ti">
                                <b>{title}</b>
                                <span>
                                    {[ti.serial_number && `SN: ${ti.serial_number}`, ti.hours != null && `${ti.hours.toLocaleString('en-US')} hrs`, ti.dealer_stock_number && `Stock #${ti.dealer_stock_number}`].filter(Boolean).join(' · ')}
                                </span>
                                <span className="u2-pl-ti-money">
                                    {ti.base_retail_value != null && <span><span className="u2-quiet">Base retail </span><b>{fmtMoney(ti.base_retail_value)}</b></span>}
                                    {over && (
                                        <span>
                                            <span className="u2-quiet">Over-allowance </span>
                                            <b className="u2-neg">{fmtMoney(ti.overallowance)}</b>{' '}
                                            <Tooltip content={PIPELINE_TIPS.overallowance}>
                                                <span className="u2-neg" tabIndex={0} aria-label="Over-allowance warning">⚠</span>
                                            </Tooltip>
                                        </span>
                                    )}
                                    {ti.net_trade_value != null && <span><span className="u2-quiet">Net trade </span><b>{fmtMoney(ti.net_trade_value)}</b></span>}
                                    {payoff && <span><span className="u2-quiet">Payoff </span><b>{fmtMoney(ti.payoff_amount)}</b></span>}
                                    {payoff && ti.trade_value_after_payoff != null && (
                                        <span><span className="u2-quiet">After payoff </span><b>{fmtMoney(ti.trade_value_after_payoff)}</b></span>
                                    )}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
TradeIns.propTypes = { tradeIns: PropTypes.array, id: PropTypes.string };

/**
 * X-axis ticks for the quote chart (QA BUG-7): distinct days only, at most
 * five, and none closer than a tenth of the span to the one before (the last
 * day always shows).
 */
export const quoteDateTicks = (days, max = 5) => {
    const distinct = [...new Set(days)].sort((a, b) => a - b);
    if (distinct.length <= 1) return distinct;
    const span = distinct[distinct.length - 1] - distinct[0];
    const stepN = Math.max(1, Math.ceil(distinct.length / max));
    const picked = [];
    distinct.forEach((d, i) => {
        const isLast = i === distinct.length - 1;
        if (!isLast && i % stepN !== 0) return;
        if (picked.length && d - picked[picked.length - 1] < span / 10) {
            if (isLast) picked[picked.length - 1] = d;
            return;
        }
        picked.push(d);
    });
    return picked;
};

/** The quote chart (P-UNIT-086, N34): dots by outcome, AI and asking lines. */
function QuoteChart({ points, aiPrice, askingPrice }) {
    const prices = points.map((p) => p.price).concat([aiPrice, askingPrice].filter((v) => v != null));
    const { lo, hi, ticks } = niceTicks(Math.min(...prices), Math.max(...prices));
    const k = (v) => `$${Math.round(v / 1000)}k`;
    const dateLabel = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
    const tip = (p) => [fmtMoney(p.price), OUTCOME_LABELS[p.outcome], p.buyer].filter(Boolean).join(' · ');
    // A time scale (QA BUG-7): quotes sit at their date, so two quotes on one
    // day share a spot and the gaps between dates are true. One label per
    // distinct date, at most five, spread evenly.
    const DAY = 86400000;
    const day = (d) => Math.floor(new Date(d).getTime() / DAY);
    const xs = points.map((p) => day(p.date));
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const pad = Math.max(2, (maxX - minX) * 0.06);
    const xticks = quoteDateTicks(xs).map((d) => [d, dateLabel(d * DAY)]);
    const refs = [];
    if (aiPrice != null) refs.push({ y: aiPrice, label: `AI ${k(aiPrice)}`, tone: 'sage', left: true });
    if (askingPrice != null) refs.push({ y: askingPrice, label: `Asking ${k(askingPrice)}`, tone: 'soft', left: true, dashed: false });
    // Labels of close lines are spread apart by the chart (charts.js).
    const outcomes = [...new Set(points.map((p) => p.outcome))];
    return (
        <>
            <UnitChart
                label={`Quoted prices for this unit over time: ${points.length} quote${points.length === 1 ? '' : 's'}${aiPrice != null ? `, AI price ${fmtMoney(aiPrice)}` : ''}${askingPrice != null ? `, asking ${fmtMoney(askingPrice)}` : ''}.`}
                height={190}
                x={[minX - pad, maxX + pad]}
                y={[lo, hi]}
                series={points.map((p, i) => [xs[i], p.price])}
                lineTone="slate"
                yticks={ticks.map((t) => [t, k(t)])}
                xticks={xticks}
                refs={refs}
                dots={points.map((p, i) => ({
                    x: xs[i], y: p.price, r: p.outcome === 'open' ? 6 : 5.5, tone: OUTCOME_TONES[p.outcome], hollow: p.outcome !== 'open', tip: tip(p),
                }))}
                readout={(i) => tip(points[i])}
                table={points.map((p) => [dateLabel(p.date), tip(p)])}
            />
            <div className="u2-legend">
                {outcomes.map((o) => (
                    <span key={o} className={`u2-tone-${OUTCOME_TONES[o]}`}>
                        <i className={`u2-pl-ldot${o === 'open' ? '' : ' is-hollow'}`} aria-hidden="true" />
                        {o === 'open' ? 'Active quote' : o === 'closed' ? 'Closed quote' : OUTCOME_LABELS[o]}
                    </span>
                ))}
                {aiPrice != null && <span className="u2-tone-sage"><i aria-hidden="true" />AI price</span>}
                {askingPrice != null && <span className="u2-tone-soft"><i aria-hidden="true" />Asking</span>}
            </div>
        </>
    );
}
QuoteChart.propTypes = { points: PropTypes.array.isRequired, aiPrice: PropTypes.number, askingPrice: PropTypes.number };

function Tile({ label, tip, children, className }) {
    return (
        <div className="u2-tile">
            <span className="u2-tile-l">{label}{tip && <Tooltip label={`About ${label}`} content={tip} />}</span>
            <span className={['u2-tile-n', className].filter(Boolean).join(' ')}>{children}</span>
        </div>
    );
}
Tile.propTypes = { label: PropTypes.string, tip: PropTypes.string, children: PropTypes.node, className: PropTypes.string };

const GAP_CLASS = { up: 'u2-pos', near: 'u2-info', mid: 'u2-warn', far: 'u2-neg', none: 'u2-quiet' };

/**
 * Pipeline on the full unit page (P-UNIT-084..094, N34): per-machine price
 * intelligence and quote chart, open deals with full offers, machine rows and
 * trade-ins, other quotes and closed deals. Today's rules and wording.
 */
export default function PipelineTabV2({ ctx }) {
    const { unit, detail, committed = false } = ctx;
    const {
        pipeline, pipelineLoading, quoteEquipment, quoteEquipmentLoading,
    } = detail;
    const [expanded, setExpanded] = useState({});
    const stock = String(unit.StockNumber);
    const aiPrice = validPrice(unit?.PredictedPrice);

    if (pipelineLoading || quoteEquipmentLoading) {
        return <StepLoader title="Loading pipeline" unit={unit} steps={PIPELINE_THINKING_STEPS} durations={PIPELINE_STEP_DURATIONS} />;
    }
    if (!pipeline && (!quoteEquipment || quoteEquipment.total_quotes === 0)) {
        return <EmptyState message="No pipeline or quote data for this unit." />;
    }

    const { byQuoteId, orphans } = groupMachineQuotes(pipeline, quoteEquipment);
    const hasQuotes = quoteEquipment && quoteEquipment.total_quotes > 0;
    const points = hasQuotes ? quoteChartPoints(quoteEquipment.quotes, stock, pipeline) : null;
    const avgGap = hasQuotes ? gapBand(quoteEquipment.avg_price_gap_pct) : null;

    return (
        <>
            {hasQuotes && (
                <section className="u2-card" aria-labelledby="u2-pl-pi">
                    <div className="u2-c-head">
                        <span className="u2-is-mark"><IronSightMark /> IronSight</span>
                        <h3 id="u2-pl-pi">Price Intelligence</h3>
                        <Tooltip label="About Price Intelligence" content={PIPELINE_TIPS.priceIntel} />
                        <span className="u2-sub">per-machine quoted prices for this unit</span>
                    </div>
                    <div className="u2-tiles-auto">
                        <Tile label="Asking Price">{fmtMoney(quoteEquipment.asking_price)}</Tile>
                        {aiPrice != null && <Tile label="AI Est. Price" tip={PIPELINE_TIPS.aiEst} className="u2-pos">{fmtMoney(aiPrice)}</Tile>}
                        <Tile label="Avg Quoted" tip={PIPELINE_TIPS.avgQuoted}>{fmtMoney(quoteEquipment.avg_quoted_price)}</Tile>
                        <Tile label="Avg Gap" tip={PIPELINE_TIPS.avgGap} className={GAP_CLASS[avgGap.band]}>{avgGap.label}</Tile>
                        <Tile label="High / Low" className="u2-pl-hilo">
                            {fmtMoney(quoteEquipment.max_quoted_price)} / {fmtMoney(quoteEquipment.min_quoted_price)}
                        </Tile>
                    </div>
                    {points && <QuoteChart points={points} aiPrice={aiPrice} askingPrice={quoteEquipment.asking_price} />}
                    {/* Selling advice: not for a committed unit, which has a buyer (R2-B2). */}
                    {!committed && withinTenPct(quoteEquipment.avg_price_gap_pct) && (
                        <p className="u2-higher"><BoltIcon /> Buyer quoted within 10% of ask — call before dropping list price</p>
                    )}
                </section>
            )}

            <div className="u2-grid2 u2-grid2--even">
                {pipeline && (
                    <section className="u2-card" aria-labelledby="u2-pl-open">
                        <div className="u2-c-head">
                            <h3 id="u2-pl-open">Open Deals ({pipeline.open.length})</h3>
                            <Tooltip label="About Open Deals" content={PIPELINE_TIPS.openDeals} />
                        </div>
                        {pipeline.open.length === 0 ? <p className="u2-note">No open deals</p> : pipeline.open.map((q) => {
                            const meetsAI = meetsAiEstimate(q, aiPrice);
                            const machines = byQuoteId[q.quote_id] ?? [];
                            const isOpen = Boolean(expanded[q.quote_id]);
                            return (
                                <div key={q.quote_id} className={`u2-pl-deal${meetsAI ? ' is-qualifying' : ''}`} data-testid="open-deal">
                                    {meetsAI && (
                                        <p className="u2-higher"><BoltIcon /> Quote meets AI estimate ({formatCurrency(aiPrice)}) — retarget this customer</p>
                                    )}
                                    <div className="u2-pl-row">
                                        <span className="u2-pl-who">
                                            <b>{q.customer || '—'}</b>
                                            <StatusPill status={q.status} />
                                            <span className="u2-quiet">{[q.salesperson, q.store].filter(Boolean).join(' · ')}</span>
                                        </span>
                                        <span className="u2-pl-amt">
                                            <b className={meetsAI ? 'u2-info' : undefined}>
                                                {q.quote_total ? `${fmtMoney(q.quote_total)}${q.is_multi_unit ? ` (${q.unit_count} units)` : ''}` : '—'}
                                            </b>
                                            <span className="u2-quiet">{q.days_ago != null ? `${q.days_ago} days ago` : '—'}</span>
                                        </span>
                                    </div>
                                    {q.is_multi_unit && (
                                        <>
                                            <button
                                                type="button"
                                                className="u2-pl-disc"
                                                aria-expanded={isOpen}
                                                onClick={() => setExpanded((prev) => ({ ...prev, [q.quote_id]: !prev[q.quote_id] }))}
                                            >
                                                {isOpen ? '▲ Hide offer' : '▼ View full offer'}
                                            </button>
                                            {isOpen && (
                                                <ul className="u2-pl-units">
                                                    {(q.units || []).map((u) => (
                                                        <li key={u.stock_number}>
                                                            <b>#{u.stock_number}</b> {[u.make, u.model, u.year].filter(Boolean).join(' ')}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </>
                                    )}
                                    {machines.map((mq, i) => <MachineRow key={mq.quote_equipment_id || i} q={mq} aiPrice={aiPrice} stock={stock} />)}
                                    <TradeIns tradeIns={q.trade_ins} id={String(q.quote_id)} />
                                </div>
                            );
                        })}
                    </section>
                )}

                {pipeline && pipeline.lost.length > 0 && (
                    <section className="u2-card" aria-labelledby="u2-pl-closed">
                        <div className="u2-c-head">
                            <h3 id="u2-pl-closed" className="u2-neg">Closed Deals ({pipeline.lost.length})</h3>
                            <Tooltip label="About Closed Deals" content={PIPELINE_TIPS.closedDeals} />
                        </div>
                        {pipeline.lost.map((q) => (
                            <div key={q.quote_id} className="u2-pl-deal is-closed" data-testid="closed-deal">
                                <div className="u2-pl-row">
                                    <span className="u2-pl-who">
                                        <b>{q.customer || '—'}</b>
                                        <span className={`u2-pl-pill is-${statusTone(q.status || 'no sale')}`}>{q.status || 'Closed'}</span>
                                        <span className="u2-quiet">{[q.salesperson, q.lost_reason].filter(Boolean).join(' · ')}</span>
                                    </span>
                                    <span className="u2-pl-amt">
                                        {q.lost_amount != null && <b>{fmtMoney(q.lost_amount)}</b>}
                                        <span className="u2-quiet">{q.lost_date || ''}</span>
                                    </span>
                                </div>
                                {(byQuoteId[q.quote_id] ?? []).map((mq, i) => <MachineRow key={mq.quote_equipment_id || i} q={mq} aiPrice={aiPrice} stock={stock} />)}
                            </div>
                        ))}
                    </section>
                )}
            </div>

            {orphans.length > 0 && (
                <section className="u2-card u2-pl-other" aria-labelledby="u2-pl-orphans">
                    <div className="u2-c-head"><h3 id="u2-pl-orphans" className="u2-quiet">Other Quotes ({orphans.length})</h3></div>
                    {orphans.map((q, i) => <MachineRow key={q.quote_equipment_id || i} q={q} aiPrice={aiPrice} stock={null} />)}
                </section>
            )}
        </>
    );
}

PipelineTabV2.propTypes = { ctx: tabCtxShape.isRequired };
